<script setup lang="ts">
import { ElCarousel } from 'element-plus'

/**
 * A slideshow of `CarouselItem`s.
 *
 * Element Plus stacks the slides absolutely and gives the track a fixed 300px, so a
 * carousel with `height` unset collapses to nothing rather than growing with its content.
 * Leave `height` out and the track is `auto` with `minHeight` as its floor — the shape a
 * strip of text slides needs, and the reason the `:deep` below lives here.
 */
const props = withDefaults(
  defineProps<{
    /** Milliseconds between slides; `0` stops the rotation. */
    interval?: number
    /** A fixed track height (`"200px"`). Omit it to let the track grow with the slide. */
    height?: string
    minHeight?: number
    arrow?: 'always' | 'hover' | 'never'
    indicatorPosition?: 'outside' | 'none' | ''
    pauseOnHover?: boolean
  }>(),
  {
    interval: 5000,
    height: undefined,
    minHeight: 150,
    arrow: 'hover',
    indicatorPosition: '',
    pauseOnHover: true
  }
)
</script>

<template>
  <ElCarousel
    class="carousel"
    :class="{ 'carousel--auto': !props.height }"
    :style="{ '--carousel-min-height': `${props.minHeight}px` }"
    :interval="interval"
    :height="height"
    :arrow="arrow"
    :indicator-position="indicatorPosition"
    :pause-on-hover="pauseOnHover"
    v-bind="$attrs"
  >
    <slot />
  </ElCarousel>
</template>

<style lang="scss" scoped>
.carousel--auto :deep(.el-carousel__container) {
  height: auto;
  min-height: var(--carousel-min-height);
}
</style>
